import axiosInstance from '@/app/api/axiosInstance';
import { Item, ItemCreate, ItemUpdate, ItemSyncResponse } from '@/app/types/item';
import { PaginatedResponse, ListQueryParams } from '@/app/types/api';

// Helper to parse numeric fields (like Decimal from backend)
const parseItemResponse = (item: Item): Item => ({
    ...item,
    conversion_factor: item.conversion_factor !== null ? parseFloat(String(item.conversion_factor)) : null,
    min_stock_level: item.min_stock_level !== null ? parseFloat(String(item.min_stock_level)) : null,
    max_stock_level: item.max_stock_level !== null ? parseFloat(String(item.max_stock_level)) : null,
    reorder_level: item.reorder_level !== null ? parseFloat(String(item.reorder_level)) : null,
});

export const listItems = async (
    params: ListQueryParams,
): Promise<PaginatedResponse<Item>> => {
    try {
        // Backend filters by org via auth
        // Add response_type=list for optimized response with only needed fields
        const response = await axiosInstance.get<PaginatedResponse<Item>>('/items/', { 
            params: {
                ...params,
                response_type: 'list'
            }
        });
        response.data.items = response.data.items.map(parseItemResponse);
        return response.data;
    } catch (error) {
        console.error('Failed to list items:', error);
        throw error;
    }
};

export const getItem = async (itemId: number): Promise<Item> => {
    try {
        const response = await axiosInstance.get<Item>(`/items/${itemId}`);
        return parseItemResponse(response.data);
    } catch (error) {
        console.error(`Failed to get item with ID ${itemId}:`, error);
        throw error;
    }
};

export const createItem = async (data: ItemCreate): Promise<Item> => {
    try {
        // Ensure numeric fields are sent correctly (e.g., as strings if backend expects Decimal)
        const payload = {
            ...data,
            conversion_factor: data.conversion_factor ? String(data.conversion_factor) : null,
            min_stock_level: data.min_stock_level ? String(data.min_stock_level) : null,
            max_stock_level: data.max_stock_level ? String(data.max_stock_level) : null,
            reorder_level: data.reorder_level ? String(data.reorder_level) : null,
        };
        const response = await axiosInstance.post<Item>('/items/', payload);
        return parseItemResponse(response.data);
    } catch (error) {
        console.error('Failed to create item:', error);
        throw error;
    }
};

export const updateItem = async (itemId: number, data: ItemUpdate): Promise<Item> => {
    try {
         // Ensure numeric fields are sent correctly (e.g., as strings if backend expects Decimal)
         const payload = {
            ...data,
            ...(data.conversion_factor !== undefined && { conversion_factor: data.conversion_factor ? String(data.conversion_factor) : null }),
            ...(data.min_stock_level !== undefined && { min_stock_level: data.min_stock_level ? String(data.min_stock_level) : null }),
            ...(data.max_stock_level !== undefined && { max_stock_level: data.max_stock_level ? String(data.max_stock_level) : null }),
            ...(data.reorder_level !== undefined && { reorder_level: data.reorder_level ? String(data.reorder_level) : null }),
        };
        const response = await axiosInstance.patch<Item>(`/items/${itemId}`, payload);
        return parseItemResponse(response.data);
    } catch (error) {
        console.error(`Failed to update item with ID ${itemId}:`, error);
        throw error;
    }
};

export const deleteItem = async (itemId: number): Promise<void> => {
    try {
        await axiosInstance.delete(`/items/${itemId}`);
    } catch (error) {
        console.error(`Failed to delete item with ID ${itemId}:`, error);
        throw error;
    }
};

// Lightweight list function for better performance
export const listItemsLightweight = async (
    params: ListQueryParams,
): Promise<PaginatedResponse<Item>> => {
    try {
        // Add include_details=false to get minimal data
        const response = await axiosInstance.get<PaginatedResponse<Item>>('/items/', { 
            params: {
                ...params,
                include_details: false
            }
        });
        return response.data;
    } catch (error) {
        console.error('Failed to list items (lightweight):', error);
        throw error;
    }
};

// Sync items function for offline sync
export const syncItems = async (
    lastSync?: string | null,
    limit: number = 1000
): Promise<ItemSyncResponse[]> => {
    try {
        const params: any = { limit };
        if (lastSync) {
            params.last_sync = lastSync;
        }

        const response = await axiosInstance.get<ItemSyncResponse[]>('/items/sync', { params });
        return response.data;
    } catch (error) {
        console.error('Failed to sync items:', error);
        throw error;
    }
};

// Bulk update items with the same data
export const bulkUpdateItemCategory = async (
    itemIds: number[],
    categoryId: number | null
): Promise<{ success: number; failed: number; errors: Array<{ itemId: number; error: string }> }> => {
    try {
        const results = await Promise.allSettled(
            itemIds.map(itemId =>
                updateItem(itemId, { category_id: categoryId })
            )
        );

        const success = results.filter(r => r.status === 'fulfilled').length;
        const failed = results.filter(r => r.status === 'rejected').length;
        const errors = results
            .map((r, index) => ({
                itemId: itemIds[index],
                error: r.status === 'rejected' ? r.reason?.message || 'Unknown error' : null
            }))
            .filter(e => e.error !== null) as Array<{ itemId: number; error: string }>;

        return { success, failed, errors };
    } catch (error) {
        console.error('Failed to bulk update items:', error);
        throw error;
    }
}; 